// src/utils/validation.ts
// Input validation and sanitization utilities

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 254;
};

/**
 * Sanitizes email by trimming and lowercasing
 */
export const sanitizeEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * Validates CPT code format (5 digits)
 */
export const validateCPTCode = (cpt: string | number): boolean => {
  const cptStr = String(cpt).trim();
  const cptRegex = /^\d{5}$/;
  return cptRegex.test(cptStr);
};

/**
 * Sanitizes CPT code by removing non-digits and trimming
 */
export const sanitizeCPTCode = (cpt: string | number): string => {
  return String(cpt).trim().replace(/[^\d]/g, '').slice(0, 5);
};

/**
 * Validates positive number
 */
export const validatePositiveNumber = (value: any): boolean => {
  const num = Number(value);
  return !isNaN(num) && num >= 0 && isFinite(num);
};

/**
 * Sanitizes number input
 */
export const sanitizeNumber = (value: any): number => {
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) return 0;
  return Math.max(0, num);
};

/**
 * Validates percentage (0-100)
 */
export const validatePercentage = (value: any): boolean => {
  const num = Number(value);
  return !isNaN(num) && num >= 0 && num <= 100 && isFinite(num);
};

/**
 * Sanitizes text to prevent XSS
 */
export const sanitizeText = (text: string, maxLength: number = 500): string => {
  return text
    .toString()
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, maxLength);
};

/**
 * Escapes HTML to prevent XSS when displaying user-generated content
 * Use this when you need to preserve formatting but prevent script injection
 */
export const escapeHtml = (unsafe: string): string => {
  if (typeof unsafe !== 'string') return '';
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validates display name (alphanumeric, spaces, hyphens, apostrophes)
 */
export const validateDisplayName = (name: string): boolean => {
  const nameRegex = /^[a-zA-Z0-9\s\-']+$/;
  return nameRegex.test(name) && name.length >= 2 && name.length <= 100;
};

/**
 * Sanitizes display name
 */
export const sanitizeDisplayName = (name: string): string => {
  return name
    .trim()
    .replace(/[^a-zA-Z0-9\s\-']/g, '')
    .substring(0, 100);
};

/**
 * Validates insurance carrier name
 */
export const validateInsuranceCarrier = (carrier: string): boolean => {
  // Allow alphanumeric, spaces, hyphens, parentheses, ampersands
  const carrierRegex = /^[a-zA-Z0-9\s\-()&]+$/;
  return carrierRegex.test(carrier) && carrier.length >= 2 && carrier.length <= 100;
};

/**
 * Sanitizes insurance carrier name
 */
export const sanitizeInsuranceCarrier = (carrier: string): string => {
  return carrier
    .trim()
    .replace(/[^a-zA-Z0-9\s\-()&]/g, '')
    .substring(0, 100);
};

/**
 * Validates CSV row data for fee schedule upload
 */
export interface CSVRowValidation {
  isValid: boolean;
  errors: string[];
  sanitizedData?: {
    cptCode: string;
    charge: number;
    description: string;
    [key: string]: any;
  };
}

export const validateCSVRow = (row: any, rowNumber: number): CSVRowValidation => {
  const errors: string[] = [];

  // Find CPT code
  const cptCode = row['CPT Code'] || row['CPT_Code'] || row['cpt'] || row['CPT'] || row['Code'];
  if (!cptCode) {
    errors.push(`Row ${rowNumber}: No CPT Code found`);
    return { isValid: false, errors };
  }

  const sanitizedCPT = sanitizeCPTCode(cptCode);
  if (!validateCPTCode(sanitizedCPT)) {
    errors.push(`Row ${rowNumber}: Invalid CPT code format: ${cptCode}`);
    return { isValid: false, errors };
  }

  // Find charge
  const charge = row['Charge'] || row['charge'] || row['CHARGE'];
  if (charge === undefined || charge === null || charge === '') {
    errors.push(`Row ${rowNumber}: No charge found for CPT ${sanitizedCPT}`);
    return { isValid: false, errors };
  }

  const sanitizedCharge = sanitizeNumber(charge);
  if (!validatePositiveNumber(sanitizedCharge)) {
    errors.push(`Row ${rowNumber}: Invalid charge ${charge} for CPT ${sanitizedCPT}`);
    return { isValid: false, errors };
  }

  // Sanitize description
  const description = row['Description'] || row['description'] || '';
  const sanitizedDescription = sanitizeText(description, 500);

  // Build sanitized data object
  const sanitizedData: any = {
    cptCode: sanitizedCPT,
    charge: sanitizedCharge,
    description: sanitizedDescription
  };

  // Sanitize all other columns (insurance carriers, indicators, etc.)
  Object.keys(row).forEach(key => {
    const normalizedKey = key.trim();

    // Skip already processed columns
    if (['CPT Code', 'CPT_Code', 'cpt', 'CPT', 'Code', 'Charge', 'charge', 'CHARGE', 'Description', 'description'].includes(normalizedKey)) {
      return;
    }

    // Sanitize value based on type
    const value = row[key];
    if (typeof value === 'number') {
      sanitizedData[normalizedKey] = sanitizeNumber(value);
    } else if (typeof value === 'string') {
      sanitizedData[normalizedKey] = sanitizeText(value, 200);
    } else {
      sanitizedData[normalizedKey] = value;
    }
  });

  return {
    isValid: true,
    errors: [],
    sanitizedData
  };
};

/**
 * Secure logging wrapper - only logs in development, never logs PHI
 * Use this instead of console.log for any calculation or business logic
 */
export const secureLog = (message: string, data?: any): void => {
  // Only log in development environment
  if (process.env.NODE_ENV === 'development') {
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
  // In production, do nothing (or send to secure audit log service)
};
